import ClientStorage from 'ClientStorage';
import { type BrowseFilters, type ProgressStatus, type StatusOption } from './BrowseCourses.types';
import { PROGRESS_OPTIONS } from './constants';
import { sanitizeSearchText } from './filterStreams';

// The keys and the URL params are the ones the Angular page used, so saved searches and links still work.
export const STORAGE_KEYS = {
    topics: 'librarySearchTopic',
    progress: 'librarySearchProgress',
    searchText: 'librarySearchText',
    selectedStatus: 'librarySelectedStatus',
} as const;

const URL_PARAMS = {
    topics: 'topics[]',
    progress: 'progress[]',
    searchText: 'searchText',
    selectedStatus: 'selectedStatus',
} as const;

type SavedFilters = { topics: unknown; progress: unknown; searchText: unknown; selectedStatus: unknown };

function readStorage(key: string): unknown {
    try {
        return JSON.parse(ClientStorage.getItem(key) ?? 'null');
    } catch {
        return null;
    }
}

function readSavedFilters(searchParams: URLSearchParams): SavedFilters {
    // URL params take precedence, so a link to a search overrides the last saved search
    const hasUrlFilters = Object.values(URL_PARAMS).some(param => searchParams.has(param));
    if (hasUrlFilters) {
        return {
            topics: searchParams.getAll(URL_PARAMS.topics),
            progress: searchParams.getAll(URL_PARAMS.progress),
            searchText: searchParams.get(URL_PARAMS.searchText),
            selectedStatus: searchParams.get(URL_PARAMS.selectedStatus),
        };
    }

    return {
        topics: readStorage(STORAGE_KEYS.topics),
        progress: readStorage(STORAGE_KEYS.progress),
        searchText: readStorage(STORAGE_KEYS.searchText),
        selectedStatus: readStorage(STORAGE_KEYS.selectedStatus),
    };
}

const asStrings = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

// Reads the saved filters and drops values that no longer exist, including a status the page hides. The
// Angular page saved progress filters by their translated name, so a saved name still maps to its option.
export function loadSavedFilters(
    searchParams: URLSearchParams,
    {
        availableTopics,
        visibleStatusOptions,
        progressNames,
    }: {
        availableTopics: string[];
        visibleStatusOptions: StatusOption[];
        progressNames: Record<ProgressStatus, string>;
    },
): BrowseFilters {
    const saved = readSavedFilters(searchParams);
    const savedTopics = asStrings(saved.topics);
    const savedProgress = asStrings(saved.progress);

    return {
        topics: availableTopics.filter(topic => savedTopics.includes(topic)),
        progress: PROGRESS_OPTIONS.filter(
            option => savedProgress.includes(option) || savedProgress.includes(progressNames[option]),
        ),
        searchText: typeof saved.searchText === 'string' ? sanitizeSearchText(saved.searchText) : '',
        selectedStatus: visibleStatusOptions.find(option => option === saved.selectedStatus) ?? 'any_status',
    };
}

export function saveFilters(
    filters: BrowseFilters,
    updateSearchParams: (updater: (p: URLSearchParams) => void) => void,
) {
    updateSearchParams(params => {
        Object.values(URL_PARAMS).forEach(param => params.delete(param));
        filters.topics.forEach(topic => params.append(URL_PARAMS.topics, topic));
        filters.progress.forEach(progress => params.append(URL_PARAMS.progress, progress));
        if (filters.searchText) params.set(URL_PARAMS.searchText, filters.searchText);
        params.set(URL_PARAMS.selectedStatus, filters.selectedStatus);
    });

    ClientStorage.setItem(STORAGE_KEYS.topics, JSON.stringify(filters.topics));
    ClientStorage.setItem(STORAGE_KEYS.progress, JSON.stringify(filters.progress));
    ClientStorage.setItem(STORAGE_KEYS.searchText, JSON.stringify(filters.searchText || null));
    ClientStorage.setItem(STORAGE_KEYS.selectedStatus, JSON.stringify(filters.selectedStatus));
}

function toggle<T>(values: T[], value: T): T[] {
    return values.includes(value) ? values.filter(v => v !== value) : [...values, value];
}

// The filter changes the controls make. Each returns the next filters.
export const filterChanges = {
    clearTopics: (current: BrowseFilters): BrowseFilters => ({ ...current, topics: [] }),
    toggleTopic: (current: BrowseFilters, topic: string): BrowseFilters => ({
        ...current,
        topics: toggle(current.topics, topic),
    }),
    clearProgress: (current: BrowseFilters): BrowseFilters => ({ ...current, progress: [] }),
    toggleProgress: (current: BrowseFilters, progress: ProgressStatus): BrowseFilters => ({
        ...current,
        progress: toggle(current.progress, progress),
    }),
    // Selecting the active status again resets it. Coming-soon courses have no progress, so that status
    // clears the progress filter.
    toggleStatus: (current: BrowseFilters, status: BrowseFilters['selectedStatus']): BrowseFilters => {
        if (current.selectedStatus === status) return { ...current, selectedStatus: 'any_status' };
        return { ...current, selectedStatus: status, progress: status === 'coming_soon' ? [] : current.progress };
    },
    setSearchText: (current: BrowseFilters, searchText: string): BrowseFilters => ({ ...current, searchText }),
};
